import { get as lodashGet, snakeCase } from 'lodash'
import sentry from '@/services/sentry'
import mixpanelContextUtils from '@/utils/mixpanel.context.utils'
import mixpanelUtil from '@/utils/mixpanel.utils.js'
import { ROUTE_NAMES } from '@/constant/routes.js'

const USER_ACTION_TYPE = 'userAction'
const ROUTE_CHANGE_ACTION_TYPE = 'routeChangeAction'
const SHOW_TOAST_ACTION_TYPE = 'showToastAction'
const ANONYMOUS_ELEMENT = 'anonymous'

const SYNC_MUTATION_TYPES = [
  'setCurrentUser',
  'updateCurrentUser',
  'setPresentation',
  'setSlideActive',
  'updateSlideActive',
  'setLang',
  'setSelectedFolderId',
  'featureToggles/setFeatures',
]

const MUTATION_STATES_MAP = {
  setCurrentUser: 'currentUser',
  updateCurrentUser: 'currentUser',
  setPresentation: 'presentation',
  setLang: 'presentation',
  updateSlideActive: 'slideActive',
  setSlideActive: 'slideActive',
  setSelectedFolderId: 'selections',
  'featureToggles/setFeatures': 'featureToggles.features',
}

const mixpanelContextSetters = {
  setCurrentUser: mixpanelContextUtils.setUserContext,
  updateCurrentUser: mixpanelContextUtils.setUserContext,
  setPresentation: mixpanelContextUtils.setPresentationContext,
  setLang: mixpanelContextUtils.setPresentationContext,
  updateSlideActive: mixpanelContextUtils.setSlideContext,
  setSlideActive: mixpanelContextUtils.setSlideContext,
  setSelectedFolderId: mixpanelContextUtils.setFolderContext,
  'featureToggles/setFeatures': mixpanelContextUtils.setFeatureFlagsContext,
}

const eventActions = {
  click: 'click',
  mouseenter: 'hover',
  dblclick: 'double_click',
  view: 'view',
  focus: 'focus',
  blur: 'blur',
  change: 'change',
  submit: 'submit',
}

const presentationContextList = [
  'presentation_id',
  'slide_id',
  'slide_type',
]

function _getObjectName(vnode) {
  const nameFromAttr = lodashGet(vnode, 'data.attrs.name', '')
  const nameFromPropsData = lodashGet(vnode, 'componentOptions.propsData.name') // This captures the name prop from component options, such as in b-form-input where "name" is passed as a prop
  const nameFromComponent = lodashGet(vnode, 'componentOptions.tag', '')

  if (!nameFromAttr && !nameFromPropsData && !nameFromComponent) {
    return ANONYMOUS_ELEMENT
  }
  const name = nameFromAttr || nameFromPropsData || nameFromComponent
  return snakeCase(name)
}

function _getOtherInfo(vnode) {
  return snakeCase(lodashGet(vnode, 'data.attrs.aha-other-info', ''))
}

function _getCustomProps(binding) {
  return lodashGet(binding, 'value')
}

function _getTrackingEventName(vnode, event) {
  const action = eventActions[event] || 'undefined_action'
  const objectName = _getObjectName(vnode)
  const otherInfo = _getOtherInfo(vnode)

  // Follow new standard for tracking event name
  // https://ahaslides.atlassian.net/wiki/spaces/AT/pages/826834947/Event+Tracking+-+Naming+Guideline+2024#C%E1%BA%A5u-tr%C3%BAc-t%C3%AAn-s%E1%BB%B1-ki%E1%BB%87n
  let args = [action, objectName, otherInfo]
  args = args.filter((arg) => arg)
  return args.join('_')
}

function _handleUserAction(action) {
  if (action.type !== USER_ACTION_TYPE) return
  const {
    eventName, vnode, binding,
  } = action.payload
  const trackingEventName = _getTrackingEventName(vnode, eventName)
  const customProps = _getCustomProps(binding)

  mixpanelUtil.track(trackingEventName, customProps)
}

function _handleRouteChangeAction(action) {
  if (action.type !== ROUTE_CHANGE_ACTION_TYPE) return
  const { to } = action.payload
  const isRenderedInsideIframe = window.top !== window.self
  if (isRenderedInsideIframe) {
    return
  }
  const routeName = snakeCase(lodashGet(to, 'name', ''))
  const trackingEventName = `view_${routeName}_page`
  mixpanelContextUtils.setRouteContext(to)
  mixpanelUtil.track(trackingEventName)
}

function _handleToastAction(action) {
  if (action.type !== SHOW_TOAST_ACTION_TYPE) return
  const { type, name } = action.payload
  const toastName = snakeCase(name)
  const trackingEventName = `view_${toastName}_${type}_toast`
  mixpanelUtil.track(trackingEventName)
}

// Ideally, when we navigate to a new presentation, we should reset the tracking context
function _handleResetPresentationContext(store, action) {
  if (action.type !== ROUTE_CHANGE_ACTION_TYPE) return
  if (!store.getters['featureToggles/canResetPresentationTrackingContext']) return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const isExitingEditorScreen = fromRoute === ROUTE_NAMES.PRESENTATION_DETAIL
  && toRoute !== ROUTE_NAMES.PRESENTATION_DETAIL

  const isExitingReportScreen = fromRoute === ROUTE_NAMES.PRESENTATION_REPORT
  && toRoute !== ROUTE_NAMES.PRESENTATION_REPORT

  const isExistingSharedReportScreen = fromRoute === ROUTE_NAMES.SHARED_REPORT
  && toRoute !== ROUTE_NAMES.SHARED_REPORT

  if (isExitingEditorScreen || isExitingReportScreen || isExistingSharedReportScreen) {
    presentationContextList.forEach((key) => {
      mixpanelUtil.unregister(key)
    })
  }
}

const mixpanelUpdateContextPlugin = (store) => {
  // Mutation that change the tracking context
  store.subscribe((mutation, state) => {
    // console.log(mutation.type)
    // console.log(state.slideActive)
    if (!SYNC_MUTATION_TYPES.includes(mutation.type)) return
    const contextSetter = mixpanelContextSetters[mutation.type]
    const contextData = state[MUTATION_STATES_MAP[mutation.type]]
                      || lodashGet(state, MUTATION_STATES_MAP[mutation.type])
    // console.log(mutation.type, ' data is: ', contextData)
    try {
      contextSetter(contextData)
    } catch (error) {
      // Ignore if the mixpanel instance has not been registered yet on the first time the app loads
      // sentry.captureException(error)
    }
  })

  // Action that change the tracking context
  store.subscribeAction((action) => {
    if (!mixpanelContextUtils.mixpanelAvailable) return
    try {
      _handleUserAction(action)
      _handleRouteChangeAction(action)
      _handleToastAction(action)
    } catch (error) {
      sentry.captureException(error)
    }
  })

  // Reset the tracking context when the user navigates from a presentation
  store.subscribeAction((action) => {
    _handleResetPresentationContext(store, action)
  })
}

export default mixpanelUpdateContextPlugin
